import React, { useState } from 'react'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { QrCode, Smartphone, Loader2 } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import api from '@/lib/api'

interface RequestAccessModalProps {
    isOpen: boolean
    onClose: () => void
}

export function RequestAccessModal({ isOpen, onClose }: RequestAccessModalProps) {
    const [patientId, setPatientId] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const { toast } = useToast()

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!patientId.trim()) return

        setIsLoading(true)
        try {
            await api.post('/mobile/access/request', { patientId })

            toast({
                title: "Request Sent",
                description: "Access request has been sent to the patient's device.",
                className: "bg-green-50 border-green-200",
            })
            onClose()
            setPatientId('')
        } catch (error: any) {
            toast({
                title: "Request Failed",
                description: error.response?.data?.message || "Could not send access request",
                variant: "destructive",
            })
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Smartphone className="h-5 w-5 text-blue-600" />
                        Connect to Patient App
                    </DialogTitle>
                    <DialogDescription>
                        Enter the patient's Secure ID displayed on their CareVault mobile app implementation.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4 py-4">
                    <div className="space-y-2">
                        <Label htmlFor="patient-id">Patient Secure ID</Label>
                        <div className="relative">
                            <QrCode className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                            <Input
                                id="patient-id"
                                placeholder="Ex. 550e8400-e29b..."
                                className="pl-9"
                                value={patientId}
                                onChange={(e) => setPatientId(e.target.value)}
                                autoComplete="off"
                            />
                        </div>
                        <p className="text-xs text-gray-500">
                            Ask the patient to open "My Health ID" in their app.
                        </p>
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={isLoading || !patientId}>
                            {isLoading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Sending...
                                </>
                            ) : (
                                'Send Request'
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}
